/* eslint-disable no-param-reassign */
import { prefersReducedMotion, hasFinePointer } from './motion-utils'

/**
 * v-magnetic — the element leans toward the cursor while hovered, then snaps
 * back on leave (the brand "magnetic button" microinteraction from the Play
 * mockups). Pair with `transition: transform var(--dur-fast) var(--ease-bounce)`
 * on the element so the release springs.
 *
 * Usage:
 *   <PlayButton v-magnetic>Claim it</PlayButton>
 *   <PlayButton v-magnetic="{ x: .25, y: .4 }">…</PlayButton>
 *
 * Binding value (optional): { x, y } pull strength per axis (default x .25,
 * y .4 — matches the mockup). No-ops on touch devices and when the user
 * prefers reduced motion.
 *
 * VUE3-MIGRATION: Vue 2 directive hooks `bind`/`unbind` become `mounted`/
 * `unmounted` in Vue 3. Rename the two keys below; the bodies are unchanged.
 */

const STORE = '__playMagnetic__'

function strengthFromBinding(binding) {
  const v = binding.value || {}
  return {
    x: typeof v.x === 'number' ? v.x : 0.25,
    y: typeof v.y === 'number' ? v.y : 0.4,
  }
}

export const magnetic = {
  bind(el, binding) {
    if (prefersReducedMotion() || !hasFinePointer()) return

    const onMove = e => {
      const { x, y } = strengthFromBinding(binding)
      const r = el.getBoundingClientRect()
      const dx = (e.clientX - r.left - r.width / 2) * x
      const dy = (e.clientY - r.top - r.height / 2) * y
      el.style.transform = `translate(${dx}px, ${dy}px)`
    }
    const onLeave = () => {
      el.style.transform = ''
    }

    el.addEventListener('mousemove', onMove)
    el.addEventListener('mouseleave', onLeave)
    el[STORE] = { onMove, onLeave }
  },

  unbind(el) {
    const handlers = el[STORE]
    if (!handlers) return
    el.removeEventListener('mousemove', handlers.onMove)
    el.removeEventListener('mouseleave', handlers.onLeave)
    el.style.transform = ''
    delete el[STORE]
  },
}

export default magnetic
